import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  buscarOperacao, atualizarOperacao, copiarOperacao,
  adicionarPalete, atualizarPalete as atualizarPaleteApi, removerPalete as removerPaleteApi,
  listarAreas, listarFiliais, salvarEnviosOperacao, enviarDocumentoOperacao, enviarDocumentoPalete,
  buscarPaleteDetalhado
} from '../../../services/montagemPalete';
import type {
  PaleteDetalhado, PaleteDetalhadoPedido, PaleteDetalhadaEtiquetaPendente
} from '../../../services/montagemPalete';
import EnvioAutomaticoConfig from '../../../components/MontagemPaletes/EnvioAutomaticoConfig';
import type { EnvioAutomaticoConfig as EnvioConfig } from '../../../types/config';
import {
  printRomaneio, printMapaCarregamento, printEtiquetas, printResumo,
  printCrachaPalete, printRelatorioPaletesCarregados,
  printRelatorioPedidosVolumesFaltantes, printRelatorioPedidosBipados,
  printRelatorioVolumesFaltantes
} from '../../../utils/printMontagemPalete';
import type { MontagemPaleteOperacao, PaleteItem, AreaEntrega, Filial, StatusOperacao, TipoPalete, StatusPalete } from '../../../types/montagemPalete';
import '../MontagemPaletes.css';

function calcularRemovidosUnicos(etiquetas: { codigo_lido: string; resultado: string }[]): number {
  const codigosOk = new Set<string>();
  const codigosRemovidos = new Set<string>();
  for (const e of etiquetas) {
    if (e.resultado === 'ok' && e.codigo_lido) codigosOk.add(e.codigo_lido);
    if (e.resultado === 'removido' && e.codigo_lido) codigosRemovidos.add(e.codigo_lido);
  }
  let count = 0;
  codigosRemovidos.forEach(c => { if (!codigosOk.has(c)) count++; });
  return count;
}

const STATUS_LABELS: Record<StatusOperacao, string> = {
  pendente: 'Pendente',
  em_conferencia: 'Em Conferência',
  em_carregamento: 'Em Carregamento',
  em_transito: 'Em Trânsito',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const STATUS_PALETE_LABELS: Record<StatusPalete, string> = {
  pendente: 'Pendente',
  pre_carregamento: 'Pré-Carregamento',
  carregado: 'Carregado',
  em_transito: 'Em Trânsito',
  descarregado: 'Descarregado',
  conferido: 'Conferido',
  inutilizado: 'Inutilizado'
};

const STATUS_PALETE_CONFIG: Record<StatusPalete, { icon: string; color: string; bg: string }> = {
  pendente: { icon: 'fa-clock', color: '#0c5460', bg: '#d1ecf1' },
  pre_carregamento: { icon: 'fa-dolly', color: '#856404', bg: '#fff3cd' },
  carregado: { icon: 'fa-truck-loading', color: '#155724', bg: '#d4edda' },
  em_transito: { icon: 'fa-shipping-fast', color: '#1a5276', bg: '#d4e6f9' },
  descarregado: { icon: 'fa-box-open', color: '#7b241c', bg: '#fadbd8' },
  conferido: { icon: 'fa-check-double', color: '#145a32', bg: '#abebc6' },
  inutilizado: { icon: 'fa-ban', color: '#721c24', bg: '#f8d7da' }
};

const STATUS_PALETE_ORDER: StatusPalete[] = ['pendente', 'pre_carregamento', 'carregado', 'em_transito', 'descarregado', 'conferido', 'inutilizado'];

// Próximo status válido para avanço (exclui cancelada que tem botão separado)
const PROXIMO_STATUS: Partial<Record<StatusOperacao, { status: StatusOperacao; label: string; icon: string; color: string }>> = {
  pendente: { status: 'em_conferencia', label: 'Iniciar Conferência', icon: 'fa-clipboard-check', color: '#3498db' },
  em_conferencia: { status: 'em_carregamento', label: 'Iniciar Carregamento', icon: 'fa-truck-loading', color: '#e67e22' },
  em_carregamento: { status: 'em_transito', label: 'Enviar em Trânsito', icon: 'fa-shipping-fast', color: '#9b59b6' },
  em_transito: { status: 'concluida', label: 'Concluir Operação', icon: 'fa-check-circle', color: '#27ae60' }
};

const MontagemPaleteView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [operacao, setOperacao] = useState<MontagemPaleteOperacao | null>(null);
  const [paletes, setPaletes] = useState<PaleteItem[]>([]);
  const [areas, setAreas] = useState<AreaEntrega[]>([]);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [showAddPalete, setShowAddPalete] = useState(false);
  const [novoPalete, setNovoPalete] = useState({ area_entrega_id: '' as string | number, doca: '', tipo_palete: 'normal' as TipoPalete, data_agendamento: '' });

  // Pallet type & scheduling
  const [showAgendamento, setShowAgendamento] = useState<number | null>(null);
  const [dataAgendamentoLocal, setDataAgendamentoLocal] = useState<string>('');

  // Envio automático
  const [enviosAutomaticos, setEnviosAutomaticos] = useState<EnvioConfig[]>([]);
  const [envioSaving, setEnvioSaving] = useState(false);
  const [envioSending, setEnvioSending] = useState(false);
  const [envioMsg, setEnvioMsg] = useState('');
  const [enviandoPaleteId, setEnviandoPaleteId] = useState<number | null>(null);

  // Pallet detail expansion (orders & labels)
  const [expandedPaleteId, setExpandedPaleteId] = useState<number | null>(null);
  const [paleteDetalhado, setPaleteDetalhado] = useState<PaleteDetalhado | null>(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [expandedPedido, setExpandedPedido] = useState<string | null>(null);

  // Multi-select mode
  const [modoSelecaoMultipla, setModoSelecaoMultipla] = useState(false);
  const [areasCheckbox, setAreasCheckbox] = useState<number[]>([]);
  const [docaPadrao, setDocaPadrao] = useState('');
  const [tipoPaletePadrao, setTipoPaletePadrao] = useState<TipoPalete>('normal');
  const [dataAgendamentoPadrao, setDataAgendamentoPadrao] = useState('');
  const [addingMultiple, setAddingMultiple] = useState(false);

  const carregarOperacao = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const result = await buscarOperacao(parseInt(id));
      if (result.success) {
        setOperacao(result.operacao);
        setPaletes(result.paletes);
      }
    } catch (error: any) {
      setErro(error?.message || 'Erro ao carregar operação');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarOperacao();
    listarFiliais({ ativo: 'true' }).then(res => {
      if (res.success) setFiliais(res.filiais);
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar filiais'));
  }, [carregarOperacao]);

  // Carregar áreas filtradas pelo cliente da operação (ou todas se sem cliente)
  useEffect(() => {
    if (operacao) {
      const params: { ativo: string; cliente_id?: number } = { ativo: 'true' };
      if (operacao.cliente_id) params.cliente_id = operacao.cliente_id;
      listarAreas(params).then(res => {
        if (res.success) setAreas(res.areas);
      }).catch((err: any) => setErro(err?.message || 'Erro ao carregar áreas'));
    }
  }, [operacao?.id, operacao?.cliente_id]);

  const handleMudarStatus = async (novoStatus: StatusOperacao) => {
    if (!id || statusLoading) return;
    setErro('');
    setStatusLoading(true);
    try {
      const result = await atualizarOperacao(parseInt(id), { status: novoStatus });
      if (result.success) {
        carregarOperacao();
      }
    } catch (error: any) {
      const msg = error?.message || 'Erro ao alterar status da operação';
      setErro(msg);
      carregarOperacao();
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCopiar = async () => {
    if (!id) return;
    if (!window.confirm('Deseja copiar esta operação com todos os paletes?')) return;
    try {
      setErro('');
      const result = await copiarOperacao(parseInt(id));
      if (result.success) {
        navigate(`/montar-carga/${result.operacao.id}`);
      }
    } catch (error: any) {
      setErro(error?.message || 'Erro ao copiar operação');
    }
  };

  const handleAdicionarPalete = async () => {
    if (!id || !novoPalete.area_entrega_id) return;
    const areaId = Number(novoPalete.area_entrega_id);
    try {
      setErro('');
      // Salvar configs de envio no banco antes de adicionar palete,
      // para que o envio automático do backend use as configs atuais da tela
      if (enviosAutomaticos.length > 0) {
        await salvarEnviosOperacao(parseInt(id), enviosAutomaticos);
      }
      await adicionarPalete(parseInt(id), {
        area_entrega_id: areaId,
        doca: novoPalete.doca || undefined,
        tipo_palete: novoPalete.tipo_palete,
        data_agendamento: novoPalete.tipo_palete === 'agendamento' && novoPalete.data_agendamento ? novoPalete.data_agendamento : undefined
      });
      setNovoPalete({ area_entrega_id: '', doca: '', tipo_palete: 'normal', data_agendamento: '' });
      setShowAddPalete(false);
      carregarOperacao();
    } catch (error: any) {
      setErro(error.message || 'Erro ao adicionar palete');
    }
  };

  const handleAlterarTipoPalete = async (paleteId: number, novoTipo: 'normal' | 'agendamento') => {
    try {
      setErro('');
      await atualizarPaleteApi(paleteId, {
        tipo_palete: novoTipo,
        ...(novoTipo === 'normal' ? { data_agendamento: '' } : {})
      });
      carregarOperacao();
    } catch (error: any) {
      setErro(error.message || 'Erro ao alterar tipo do palete');
    }
  };

  const handleAlterarDataAgendamento = async (paleteId: number, dataAgendamento: string) => {
    try {
      setErro('');
      await atualizarPaleteApi(paleteId, { data_agendamento: dataAgendamento || '' });
      carregarOperacao();
    } catch (error: any) {
      setErro(error.message || 'Erro ao salvar data de agendamento');
    }
  };

  const handleRemoverPalete = async (paleteId: number) => {
    if (!window.confirm('Tem certeza que deseja remover este palete?')) return;
    try {
      setErro('');
      await removerPaleteApi(paleteId);
      carregarOperacao();
    } catch (error: any) {
      setErro(error?.message || 'Erro ao remover palete');
    }
  };

  const handleExpandPalete = async (paleteId: number) => {
    if (expandedPaleteId === paleteId) {
      setExpandedPaleteId(null);
      setPaleteDetalhado(null);
      setExpandedPedido(null);
      return;
    }
    setExpandedPaleteId(paleteId);
    setExpandedPedido(null);
    setLoadingDetalhe(true);
    try {
      const result = await buscarPaleteDetalhado(paleteId);
      if (result.success) {
        setPaleteDetalhado(result.palete);
      }
    } catch (err: any) {
      setErro(err?.message || 'Erro ao carregar detalhes do palete');
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const handleExpandPedido = (numeroPedido: string) => {
    setExpandedPedido(expandedPedido === numeroPedido ? null : numeroPedido);
  };

  const handleAlterarStatusPalete = async (paleteId: number, novoStatus: StatusPalete) => {
    try {
      setErro('');
      await atualizarPaleteApi(paleteId, { status: novoStatus });
      carregarOperacao();
    } catch (error: any) {
      setErro(error.message || 'Erro ao alterar status do palete');
    }
  };

  const handleSalvarEnvios = async () => {
    if (!id) return;
    setEnvioSaving(true);
    setEnvioMsg('');
    try {
      await salvarEnviosOperacao(parseInt(id), enviosAutomaticos);
      setEnvioMsg('Configurações de envio salvas com sucesso!');
      setTimeout(() => setEnvioMsg(''), 3000);
    } catch (error: any) {
      setEnvioMsg(`Erro ao salvar: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setEnvioSaving(false);
    }
  };

  const handleEnviarPalete = async (paleteId: number, numeroPalete: number) => {
    if (enviosAutomaticos.length === 0) {
      setEnvioMsg('Configure o envio automático antes de enviar documentos.');
      setTimeout(() => setEnvioMsg(''), 3000);
      return;
    }
    setEnviandoPaleteId(paleteId);
    try {
      await enviarDocumentoPalete(paleteId, enviosAutomaticos);
      setEnvioMsg(`Documentos do Palete #${numeroPalete} enviados com sucesso!`);
      setTimeout(() => setEnvioMsg(''), 5000);
    } catch (error: any) {
      setEnvioMsg(`Erro ao enviar palete #${numeroPalete}: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setEnviandoPaleteId(null);
    }
  };

  const handleEnviarAgora = async () => {
    if (!id || enviosAutomaticos.length === 0) return;
    if (!window.confirm('Deseja enviar os documentos configurados agora?')) return;
    setEnvioSending(true);
    setEnvioMsg('');
    try {
      await enviarDocumentoOperacao(parseInt(id), enviosAutomaticos);
      setEnvioMsg('Documentos enviados com sucesso!');
      setTimeout(() => setEnvioMsg(''), 5000);
    } catch (error: any) {
      setEnvioMsg(`Erro ao enviar: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setEnvioSending(false);
    }
  };

  // Multi-select helpers
  const areasJaAdicionadas = new Set(paletes.map(p => p.area_entrega_id));

  const toggleAreaCheckbox = (areaId: number) => {
    setAreasCheckbox(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const handleAdicionarMultiplos = async () => {
    if (!id || areasCheckbox.length === 0) return;
    setAddingMultiple(true);
    setErro('');
    // Salvar configs de envio no banco antes de adicionar paletes,
    // para que o envio automático do backend use as configs atuais da tela
    if (enviosAutomaticos.length > 0) {
      try {
        await salvarEnviosOperacao(parseInt(id), enviosAutomaticos);
      } catch {
        // Continuar mesmo se falhar o save dos envios
      }
    }
    let adicionados = 0;
    let erros = 0;

    for (const areaId of areasCheckbox) {
      if (areasJaAdicionadas.has(areaId)) continue;
      try {
        await adicionarPalete(parseInt(id), {
          area_entrega_id: areaId,
          doca: docaPadrao || undefined,
          tipo_palete: tipoPaletePadrao,
          data_agendamento: tipoPaletePadrao === 'agendamento' && dataAgendamentoPadrao ? dataAgendamentoPadrao : undefined
        });
        adicionados++;
      } catch {
        erros++;
      }
    }

    setAreasCheckbox([]);
    setDocaPadrao('');
    setTipoPaletePadrao('normal');
    setDataAgendamentoPadrao('');
    setModoSelecaoMultipla(false);
    setAddingMultiple(false);
    carregarOperacao();

    if (erros > 0) {
      setErro(`${adicionados} palete(s) adicionado(s), ${erros} erro(s).`);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando operação...</p>
        </div>
      </div>
    );
  }

  if (!operacao) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ddd', marginBottom: 16, display: 'block' }}></i>
          <p>Operação não encontrada</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/montar-carga')}>
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  // Filtrar áreas pela filial da operação
  const areasFiltradas = operacao.filial_id
    ? areas.filter(a => !a.filial_id || a.filial_id === operacao.filial_id)
    : areas;

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate('/montar-carga')}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      {/* Cabeçalho da Operação */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>
            <i className="fas fa-pallet" style={{ marginRight: 8, color: '#3498db' }}></i>
            Operação #{operacao.numero_operacao}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className={`status-badge status-montagem-${operacao.status}`}>
              {STATUS_LABELS[operacao.status]}
            </span>
          </div>
        </div>
        <div className="card-body">
          <div className="operacao-info-grid">
            <div className="operacao-info-item">
              <span className="info-label">Cliente</span>
              <span className="info-value">{operacao.cliente_razao_social || '-'}</span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Filial</span>
              <span className="info-value">{operacao.filial_nome || '-'}</span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Local Pré-Carregamento</span>
              <span className="info-value">
                {operacao.local_pre_carregamento === 'cliente' ? (
                  <><i className="fas fa-building" style={{ marginRight: 6, color: '#7f8c8d' }}></i>No Cliente</>
                ) : (
                  <><i className="fas fa-warehouse" style={{ marginRight: 6, color: '#7f8c8d' }}></i>Base da Transportadora</>
                )}
              </span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Data de Emissão</span>
              <span className="info-value">
                {new Date(operacao.data_emissao).toLocaleDateString('pt-BR')} às {new Date(operacao.data_emissao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Conferente</span>
              <span className="info-value">{operacao.conferente_nome || '-'}</span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Criado por</span>
              <span className="info-value">{operacao.criado_por_nome || '-'}</span>
            </div>
            <div className="operacao-info-item">
              <span className="info-label">Total de Paletes</span>
              <span className="info-value" style={{ fontSize: '1.2rem', fontWeight: 700, color: '#8e44ad' }}>
                {paletes.length}
              </span>
            </div>
          </div>

          {operacao.observacoes && (
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, marginTop: 8 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7f8c8d', textTransform: 'uppercase' as const }}>Observações</span>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem' }}>{operacao.observacoes}</p>
            </div>
          )}

          {/* Mensagem de Erro */}
          {erro && (
            <div className="montagem-error" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-exclamation-circle"></i>
              <span>{erro}</span>
              <button onClick={() => setErro('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1rem' }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          {/* Ações */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {/* Botão de avanço de status */}
            {PROXIMO_STATUS[operacao.status] && (
              <button
                className="btn btn-sm"
                style={{ backgroundColor: PROXIMO_STATUS[operacao.status]!.color, color: '#fff', border: 'none' }}
                onClick={() => {
                  const proximo = PROXIMO_STATUS[operacao.status]!;
                  if (window.confirm(`Deseja avançar para "${STATUS_LABELS[proximo.status]}"?`)) {
                    handleMudarStatus(proximo.status);
                  }
                }}
                disabled={statusLoading}
              >
                <i className={`fas ${statusLoading ? 'fa-spinner fa-spin' : PROXIMO_STATUS[operacao.status]!.icon}`}></i>{' '}
                {PROXIMO_STATUS[operacao.status]!.label}
              </button>
            )}
            {operacao.status !== 'concluida' && operacao.status !== 'cancelada' && (
              <button className="btn btn-danger btn-sm" onClick={() => {
                if (window.confirm('Tem certeza que deseja cancelar esta operação?')) {
                  handleMudarStatus('cancelada');
                }
              }} disabled={statusLoading}>
                <i className={`fas ${statusLoading ? 'fa-spinner fa-spin' : 'fa-times'}`}></i> Cancelar
              </button>
            )}
            {(operacao.status === 'pendente' || operacao.status === 'em_conferencia') && (
              <button className="btn btn-outline btn-sm" onClick={() => navigate(`/montar-carga/${id}/editar`)}>
                <i className="fas fa-edit"></i> Editar
              </button>
            )}
            <button className="btn btn-outline btn-sm" onClick={handleCopiar}>
              <i className="fas fa-copy"></i> Copiar Operação
            </button>
          </div>
        </div>
      </div>

      {/* Paletes */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-boxes-stacked" style={{ marginRight: 8, color: '#8e44ad' }}></i>
            Paletes ({paletes.length})
          </h3>
          {(operacao.status === 'pendente' || operacao.status === 'em_conferencia') && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => { setShowAddPalete(!showAddPalete); setModoSelecaoMultipla(false); }}>
                <i className={`fas ${showAddPalete ? 'fa-minus' : 'fa-plus'}`}></i>
                {showAddPalete ? 'Fechar' : 'Adicionar Palete'}
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Formulário para adicionar palete */}
          {showAddPalete && (
            <div className="palete-form-inline" style={{ marginBottom: 16 }}>
              {/* Toggle entre modo individual e múltiplo */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setModoSelecaoMultipla(!modoSelecaoMultipla)}
                  style={{ fontSize: '0.8rem' }}
                >
                  <i className={`fas ${modoSelecaoMultipla ? 'fa-minus' : 'fa-th-list'}`}></i>
                  {modoSelecaoMultipla ? 'Modo Individual' : 'Selecionar Várias Áreas'}
                </button>
              </div>

              {modoSelecaoMultipla ? (
                /* Modo seleção múltipla */
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Selecione as áreas ({areasCheckbox.length} selecionada{areasCheckbox.length !== 1 ? 's' : ''})
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      onClick={() => {
                        const disponiveis = areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id));
                        setAreasCheckbox(areasCheckbox.length === disponiveis.length ? [] : disponiveis.map(a => a.id));
                      }}
                      style={{ fontSize: '0.75rem' }}
                    >
                      {areasCheckbox.length === areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id)).length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                    </button>
                  </div>

                  {areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id)).length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: '#7f8c8d', fontStyle: 'italic', padding: '12px 0' }}>
                      Todas as áreas disponíveis já foram adicionadas.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 250, overflowY: 'auto', padding: 4 }}>
                      {areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id)).map(area => (
                        <label
                          key={area.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            borderRadius: 8, border: `2px solid ${areasCheckbox.includes(area.id) ? '#3498db' : '#dce1e6'}`,
                            background: areasCheckbox.includes(area.id) ? '#ebf5fb' : 'white',
                            cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={areasCheckbox.includes(area.id)}
                            onChange={() => toggleAreaCheckbox(area.id)}
                          />
                          <span>
                            {area.nome}
                            {area.regiao && <span style={{ color: '#7f8c8d' }}> ({area.regiao})</span>}
                            {area.filial_id && !operacao.filial_id && (
                              <span style={{ color: '#95a5a6', fontSize: '0.75rem', marginLeft: 4 }}>
                                [{filiais.find(f => f.id === area.filial_id)?.nome_fantasia || ''}]
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="form-row" style={{ marginTop: 12 }}>
                    <div className="form-group">
                      <label>Doca (aplicar a todos)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={docaPadrao}
                        onChange={(e) => setDocaPadrao(e.target.value)}
                        placeholder="Ex: DOCA 01"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tipo (aplicar a todos)</label>
                      <select
                        className="form-control"
                        value={tipoPaletePadrao}
                        onChange={(e) => setTipoPaletePadrao(e.target.value as TipoPalete)}
                      >
                        <option value="normal">Normal</option>
                        <option value="agendamento">Agendamento</option>
                      </select>
                    </div>
                    {tipoPaletePadrao === 'agendamento' && (
                      <div className="form-group">
                        <label>Data/Hora Agendamento</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={dataAgendamentoPadrao}
                          onChange={(e) => setDataAgendamentoPadrao(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowAddPalete(false); setModoSelecaoMultipla(false); setAreasCheckbox([]); }}>
                      Cancelar
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleAdicionarMultiplos} disabled={areasCheckbox.length === 0 || addingMultiple}>
                      {addingMultiple
                        ? <><i className="fas fa-spinner fa-spin"></i> Adicionando...</>
                        : <><i className="fas fa-plus"></i> Adicionar {areasCheckbox.length} Palete{areasCheckbox.length !== 1 ? 's' : ''}</>
                      }
                    </button>
                  </div>
                </>
              ) : (
                /* Modo individual */
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Área de Entrega *</label>
                      <select
                        className="form-control"
                        value={novoPalete.area_entrega_id}
                        onChange={(e) => setNovoPalete(prev => ({ ...prev, area_entrega_id: e.target.value }))}
                      >
                        <option value="">Selecione uma área</option>
                        {areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id)).map(area => (
                          <option key={area.id} value={area.id}>
                            {area.nome} {area.regiao ? `(${area.regiao})` : ''}
                            {area.filial_id && !operacao.filial_id ? ` [${filiais.find(f => f.id === area.filial_id)?.nome_fantasia || ''}]` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Doca</label>
                      <input
                        type="text"
                        className="form-control"
                        value={novoPalete.doca}
                        onChange={(e) => setNovoPalete(prev => ({ ...prev, doca: e.target.value }))}
                        placeholder="Ex: DOCA 01"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tipo</label>
                      <select
                        className="form-control"
                        value={novoPalete.tipo_palete}
                        onChange={(e) => setNovoPalete(prev => ({ ...prev, tipo_palete: e.target.value as TipoPalete }))}
                      >
                        <option value="normal">Normal</option>
                        <option value="agendamento">Agendamento</option>
                      </select>
                    </div>
                    {novoPalete.tipo_palete === 'agendamento' && (
                      <div className="form-group">
                        <label>Data/Hora Agendamento</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={novoPalete.data_agendamento}
                          onChange={(e) => setNovoPalete(prev => ({ ...prev, data_agendamento: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowAddPalete(false)}>
                      Cancelar
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleAdicionarPalete} disabled={!novoPalete.area_entrega_id}>
                      <i className="fas fa-plus"></i> Adicionar
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Grid de Paletes */}
          {paletes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>
              <i className="fas fa-boxes-stacked" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: 12, display: 'block' }}></i>
              <p>Nenhum palete adicionado</p>
            </div>
          ) : (
            <div className="paletes-grid">
              {paletes.map((p) => (
                <div key={p.id} className={`palete-card${p.tipo_palete === 'agendamento' ? ' palete-agendamento' : ''}${expandedPaleteId === p.id ? ' palete-expanded' : ''}`}>
                  {(operacao.status === 'pendente' || operacao.status === 'em_conferencia') && (
                    <button className="palete-remove" onClick={() => handleRemoverPalete(p.id)}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                  <div className="palete-header">
                    <span className="palete-number">
                      <i className="fas fa-pallet"></i>
                      Palete #{p.numero_palete}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {(() => {
                        const paleteStatus = (p.status || 'pendente') as StatusPalete;
                        const cfg = STATUS_PALETE_CONFIG[paleteStatus];
                        return (
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, fontWeight: 600,
                            background: cfg.bg, color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: 4
                          }}>
                            <i className={`fas ${cfg.icon}`} style={{ fontSize: '0.65rem' }}></i>
                            {STATUS_PALETE_LABELS[paleteStatus]}
                          </span>
                        );
                      })()}
                      {p.tipo_palete === 'agendamento' && (
                        <span className="palete-tipo-badge agendamento">
                          <i className="fas fa-calendar-check"></i> Agendamento
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="palete-info">
                    <div className="palete-info-row">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>Área: <strong>{p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}</strong></span>
                    </div>
                    {p.area_regiao && (
                      <div className="palete-info-row">
                        <i className="fas fa-globe-americas"></i>
                        <span>Região: <strong>{p.area_regiao}</strong></span>
                      </div>
                    )}
                    {p.doca && (
                      <div className="palete-info-row">
                        <i className="fas fa-warehouse"></i>
                        <span>Doca: <strong>{p.doca}</strong></span>
                      </div>
                    )}
                    {p.tipo_palete === 'agendamento' && (
                      <div className="palete-info-row" style={{ color: '#e67e22' }}>
                        <i className="fas fa-truck"></i>
                        <span><strong>Envio direto ao destinatário</strong></span>
                      </div>
                    )}
                    {p.tipo_palete === 'agendamento' && p.data_agendamento && (
                      <div className="palete-info-row" style={{ color: '#8e44ad' }}>
                        <i className="fas fa-clock"></i>
                        <span>Agendamento: <strong>
                          {new Date(p.data_agendamento).toLocaleDateString('pt-BR')} às {new Date(p.data_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </strong></span>
                      </div>
                    )}
                  </div>

                  {/* Status do Palete */}
                  <div className="palete-status-selector">
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#7f8c8d', textTransform: 'uppercase' as const }}>Status:</span>
                    <div className="palete-status-pills">
                      {STATUS_PALETE_ORDER.map((s) => {
                        const cfg = STATUS_PALETE_CONFIG[s];
                        const isActive = (p.status || 'pendente') === s;
                        return (
                          <button
                            key={s}
                            className={`palete-status-pill ${isActive ? 'active' : ''}`}
                            style={{
                              background: isActive ? cfg.bg : 'transparent',
                              color: isActive ? cfg.color : '#95a5a6',
                              borderColor: isActive ? cfg.color : '#dce1e6'
                            }}
                            onClick={() => handleAlterarStatusPalete(p.id, s)}
                            title={STATUS_PALETE_LABELS[s]}
                          >
                            <i className={`fas ${cfg.icon}`}></i>
                            <span className="pill-label">{STATUS_PALETE_LABELS[s]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Botão Ver Pedidos */}
                  <div className="palete-actions">
                    <button
                      className={`palete-ver-pedidos-btn ${expandedPaleteId === p.id ? 'active' : ''}`}
                      onClick={() => handleExpandPalete(p.id)}
                    >
                      <i className={`fas ${loadingDetalhe && expandedPaleteId === p.id ? 'fa-spinner fa-spin' : expandedPaleteId === p.id ? 'fa-chevron-up' : 'fa-boxes-stacked'}`}></i>
                      <span>{expandedPaleteId === p.id ? 'Ocultar Pedidos' : 'Ver Pedidos'}</span>
                    </button>
                    {(operacao.status === 'pendente' || operacao.status === 'em_conferencia') && (
                      <>
                        <button
                          className={`palete-tipo-toggle ${p.tipo_palete === 'agendamento' ? 'active' : ''}`}
                          onClick={() => handleAlterarTipoPalete(p.id, p.tipo_palete === 'agendamento' ? 'normal' : 'agendamento')}
                          title={p.tipo_palete === 'agendamento' ? 'Mudar para Normal' : 'Mudar para Agendamento'}
                        >
                          <i className={`fas ${p.tipo_palete === 'agendamento' ? 'fa-undo' : 'fa-calendar-check'}`}></i>
                          <span>{p.tipo_palete === 'agendamento' ? 'Normal' : 'Agendamento'}</span>
                        </button>
                        {p.tipo_palete === 'agendamento' && (
                          <button
                            className={`palete-agendamento-btn ${showAgendamento === p.id ? 'active' : ''}`}
                            onClick={() => {
                              if (showAgendamento === p.id) {
                                setShowAgendamento(null);
                              } else {
                                setDataAgendamentoLocal(p.data_agendamento ? new Date(p.data_agendamento).toISOString().slice(0, 16) : '');
                                setShowAgendamento(p.id);
                              }
                            }}
                            title="Definir data e hora de agendamento"
                          >
                            <i className="fas fa-clock"></i>
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Envio individual do palete */}
                  {enviosAutomaticos.length > 0 && (
                    <div style={{ padding: '4px 8px', borderTop: '1px solid #eee' }}>
                      <button
                        className="btn btn-sm"
                        style={{
                          fontSize: '0.72rem', padding: '3px 8px', width: '100%',
                          backgroundColor: enviandoPaleteId === p.id ? '#95a5a6' : '#27ae60',
                          color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer'
                        }}
                        disabled={enviandoPaleteId === p.id}
                        onClick={() => handleEnviarPalete(p.id, p.numero_palete)}
                        title="Enviar documentos deste palete via WhatsApp/Email"
                      >
                        <i className={`fas ${enviandoPaleteId === p.id ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>{' '}
                        {enviandoPaleteId === p.id ? 'Enviando...' : 'Enviar Documentos'}
                      </button>
                    </div>
                  )}

                  {/* Campo de data/hora de agendamento (oculto por padrão) */}
                  {showAgendamento === p.id && p.tipo_palete === 'agendamento' && (
                    <div className="palete-agendamento-form">
                      <label>Data e hora do agendamento</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={dataAgendamentoLocal}
                        onChange={(e) => setDataAgendamentoLocal(e.target.value)}
                        onBlur={() => {
                          const valorAtual = p.data_agendamento ? new Date(p.data_agendamento).toISOString().slice(0, 16) : '';
                          if (dataAgendamentoLocal !== valorAtual) {
                            handleAlterarDataAgendamento(p.id, dataAgendamentoLocal);
                          }
                        }}
                      />
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <button
                          className="btn btn-sm btn-primary"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => handleAlterarDataAgendamento(p.id, dataAgendamentoLocal)}
                        >
                          <i className="fas fa-check"></i> Salvar
                        </button>
                        {p.data_agendamento && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => {
                              setDataAgendamentoLocal('');
                              handleAlterarDataAgendamento(p.id, '');
                            }}
                          >
                            <i className="fas fa-times"></i> Limpar
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Detalhe expandido - Pedidos e Etiquetas */}
                  {expandedPaleteId === p.id && (
                    <div className="palete-detalhe-expandido">
                      {loadingDetalhe ? (
                        <div style={{ textAlign: 'center', padding: 20, color: '#7f8c8d' }}>
                          <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                          Carregando detalhes...
                        </div>
                      ) : paleteDetalhado ? (
                        <PaleteDetalheInline
                          palete={paleteDetalhado}
                          expandedPedido={expandedPedido}
                          onExpandPedido={handleExpandPedido}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: 16, color: '#e74c3c', fontSize: '0.85rem' }}>
                          Erro ao carregar detalhes
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Documentos e Relatórios */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-print" style={{ marginRight: 8, color: '#e67e22' }}></i>
            Documentos e Relatórios
          </h3>
        </div>
        <div className="card-body">
          {/* Documentos da Operação */}
          <div style={{ marginBottom: 20 }}>
            <h4 className="docs-section-title">
              <i className="fas fa-file-alt"></i> Documentos da Operação
            </h4>
            <div className="docs-grid">
              <button className="doc-card" onClick={() => printRomaneio({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#ebf5fb', color: '#3498db' }}>
                  <i className="fas fa-clipboard-list"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Romaneio de Paletes</span>
                  <span className="doc-desc">Lista completa de todos os paletes com áreas, regiões e docas</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printMapaCarregamento({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#fef9e7', color: '#f39c12' }}>
                  <i className="fas fa-map"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Mapa de Carregamento</span>
                  <span className="doc-desc">Visualização dos paletes organizados por doca</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printEtiquetas({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#f4ecf7', color: '#8e44ad' }}>
                  <i className="fas fa-tags"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Etiquetas de Paletes</span>
                  <span className="doc-desc">Etiquetas individuais para identificação de cada palete</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printResumo({ operacao, paletes })}>
                <div className="doc-icon" style={{ background: '#eafaf1', color: '#27ae60' }}>
                  <i className="fas fa-chart-bar"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Resumo da Operação</span>
                  <span className="doc-desc">
                    Resumo com estatísticas por área e doca
                    {paletes.length > 0 && (() => {
                      const totalOk = paletes.reduce((sum, p) => sum + (p.total_leituras_ok || 0), 0);
                      const totalLeituras = paletes.reduce((sum, p) => sum + (p.total_leituras || 0), 0);
                      const pct = totalLeituras > 0 ? (totalOk / totalLeituras) * 100 : 0;
                      const color = pct === 100 ? '#27ae60' : pct >= 75 ? '#f39c12' : '#e74c3c';
                      return (
                        <span style={{ display: 'block', marginTop: 4, fontWeight: 700, color }}>
                          <i className="fas fa-check-circle" style={{ marginRight: 4 }}></i>
                          {totalOk}/{totalLeituras} conferidos ({pct.toFixed(2).replace('.', ',')}%)
                        </span>
                      );
                    })()}
                  </span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>
            </div>
          </div>

          {/* Relatórios Extras */}
          <div>
            <h4 className="docs-section-title">
              <i className="fas fa-chart-pie"></i> Relatórios
            </h4>
            <div className="docs-grid">
              <button className="doc-card" onClick={() => printRelatorioPaletesCarregados({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#d4edda', color: '#155724' }}>
                  <i className="fas fa-truck-loading"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Paletes Carregados</span>
                  <span className="doc-desc">Relatório de controle de paletes carregados com campo de visto</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printRelatorioPedidosVolumesFaltantes({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#fff3cd', color: '#856404' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Pedidos e Volumes Faltantes</span>
                  <span className="doc-desc">Controle de pedidos e volumes faltantes por palete</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printRelatorioPedidosBipados({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}>
                  <i className="fas fa-barcode"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Pedidos Bipados</span>
                  <span className="doc-desc">Registro de pedidos lidos/conferidos por palete</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printRelatorioVolumesFaltantes({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#f8d7da', color: '#721c24' }}>
                  <i className="fas fa-box-open"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Volumes Faltantes</span>
                  <span className="doc-desc">Registro detalhado de volumes faltantes identificados</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>

              <button className="doc-card" onClick={() => printCrachaPalete({ operacao, paletes })} disabled={paletes.length === 0}>
                <div className="doc-icon" style={{ background: '#e8eaf6', color: '#3f51b5' }}>
                  <i className="fas fa-id-badge"></i>
                </div>
                <div className="doc-info">
                  <span className="doc-title">Crachá Palete</span>
                  <span className="doc-desc">Crachás de identificação para fixar nos paletes</span>
                </div>
                <i className="fas fa-print doc-action"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Envio Automático de Documentos */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-paper-plane" style={{ marginRight: 8, color: '#27ae60' }}></i>
            Envio Automático de Documentos
          </h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: 12 }}>
            Configure o envio automático de documentos via WhatsApp ou Email. Novos paletes adicionados no pré-carregamento serão enviados automaticamente.
          </p>

          <EnvioAutomaticoConfig
            operacaoId={parseInt(id!)}
            envios={enviosAutomaticos}
            onChange={setEnviosAutomaticos}
          />

          {envioMsg && (
            <div style={{
              padding: '8px 12px', borderRadius: '6px', marginTop: '12px', fontSize: '0.85rem',
              background: envioMsg.includes('Erro') ? '#f8d7da' : '#d4edda',
              color: envioMsg.includes('Erro') ? '#721c24' : '#155724'
            }}>
              {envioMsg}
            </div>
          )}

          {enviosAutomaticos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSalvarEnvios}
                disabled={envioSaving}
              >
                <i className={`fas ${envioSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>{' '}
                {envioSaving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
              {paletes.length > 0 && (
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: '#27ae60', color: '#fff', border: 'none' }}
                  onClick={handleEnviarAgora}
                  disabled={envioSending}
                >
                  <i className={`fas ${envioSending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>{' '}
                  {envioSending ? 'Enviando...' : 'Enviar Agora'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================
// Componente de detalhe inline do palete (pedidos e etiquetas)
// =============================================

const RESULTADO_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  ok: { label: 'OK', color: '#27ae60', icon: 'fas fa-check-circle' },
  duplicado: { label: 'Duplicado', color: '#e67e22', icon: 'fas fa-clone' },
  removido: { label: 'Removido', color: '#95a5a6', icon: 'fas fa-trash' },
  nao_encontrado: { label: 'Não encontrado', color: '#e74c3c', icon: 'fas fa-times-circle' },
  pendente: { label: 'Pendente', color: '#c0392b', icon: 'fas fa-hourglass-half' },
};

interface PaleteDetalheInlineProps {
  palete: PaleteDetalhado;
  expandedPedido: string | null;
  onExpandPedido: (numeroPedido: string) => void;
}

const PaleteDetalheInline: React.FC<PaleteDetalheInlineProps> = ({ palete, expandedPedido, onExpandPedido }) => {
  const resumo = palete.resumo;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Calcular totais de bipados e pendentes
  const totalEsperado = palete.pedidos.reduce((acc, p) => acc + (p.total_volumes_esperado || 0), 0);
  const totalBipados = palete.pedidos.reduce((acc, p) => acc + p.total_lido, 0);
  const totalPendentes = totalEsperado > 0 ? Math.max(0, totalEsperado - totalBipados) : 0;

  return (
    <div>
      {/* Resumo */}
      <div className="palete-detalhe-resumo">
        <div className="palete-detalhe-chip">
          <i className="fas fa-barcode" style={{ color: '#27ae60' }}></i>
          <div>
            <span className="chip-label">Bipados</span>
            <span className="chip-value">{totalBipados}</span>
          </div>
        </div>
        <div className="palete-detalhe-chip">
          <i className="fas fa-hourglass-half" style={{ color: totalPendentes > 0 ? '#e74c3c' : '#95a5a6' }}></i>
          <div>
            <span className="chip-label">Pendentes</span>
            <span className="chip-value" style={{ color: totalPendentes > 0 ? '#e74c3c' : '#2c3e50' }}>{totalPendentes}</span>
          </div>
        </div>
        <div className="palete-detalhe-chip">
          <i className="fas fa-clone" style={{ color: '#e67e22' }}></i>
          <div>
            <span className="chip-label">Duplicados</span>
            <span className="chip-value">{resumo.leituras_duplicadas}</span>
          </div>
        </div>
        <div className="palete-detalhe-chip">
          <i className="fas fa-file-invoice" style={{ color: '#8e44ad' }}></i>
          <div>
            <span className="chip-label">Pedidos</span>
            <span className="chip-value">{resumo.total_pedidos}</span>
          </div>
        </div>
        <div className="palete-detalhe-chip">
          <i className="fas fa-weight-hanging" style={{ color: '#3498db' }}></i>
          <div>
            <span className="chip-label">Peso</span>
            <span className="chip-value">{resumo.peso_total} kg</span>
          </div>
        </div>
        {totalEsperado > 0 && (
          <div className="palete-detalhe-chip">
            <i className="fas fa-tasks" style={{ color: totalPendentes === 0 ? '#27ae60' : '#e67e22' }}></i>
            <div>
              <span className="chip-label">Progresso</span>
              <span className="chip-value" style={{ color: totalPendentes === 0 ? '#27ae60' : '#2c3e50' }}>
                {totalEsperado > 0 ? Math.round((totalBipados / totalEsperado) * 100) : 0}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Pedidos */}
      {palete.pedidos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 16, color: '#7f8c8d', fontSize: '0.85rem' }}>
          <i className="fas fa-inbox" style={{ marginRight: 6 }}></i>
          Nenhuma leitura registrada
        </div>
      ) : (
        <div className="palete-detalhe-pedidos">
          <div className="palete-detalhe-pedidos-header">
            <i className="fas fa-file-invoice" style={{ marginRight: 6, color: '#7f8c8d' }}></i>
            Pedidos ({palete.pedidos.length})
            {totalEsperado > 0 && (
              <span style={{ marginLeft: 8, fontWeight: 400, fontSize: '0.75rem', color: '#7f8c8d' }}>
                — {totalBipados} bipados, {totalPendentes} pendentes de {totalEsperado} esperados
              </span>
            )}
          </div>
          {palete.pedidos.map((pedido) => (
            <PedidoRowInline
              key={pedido.numero_pedido}
              pedido={pedido}
              etiquetasPendentes={(palete.etiquetas_pendentes || []).filter(e => e.numero_pedido === pedido.numero_pedido)}
              isExpanded={expandedPedido === pedido.numero_pedido}
              onToggle={() => onExpandPedido(pedido.numero_pedido)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Ocorrências */}
      {palete.ocorrencias.length > 0 && (
        <div className="palete-detalhe-ocorrencias">
          <div className="palete-detalhe-ocorrencias-header">
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
            Ocorrências ({palete.ocorrencias.length})
          </div>
          {palete.ocorrencias.map((oc) => (
            <div key={oc.id} className="palete-ocorrencia-item">
              <span className="palete-ocorrencia-tipo">{oc.tipo_nome}</span>
              {oc.numero_pedido && (
                <span style={{ color: '#7f8c8d', fontSize: '0.8rem' }}>Pedido: <strong>{oc.numero_pedido}</strong></span>
              )}
              {oc.quantidade_divergente > 0 && (
                <span style={{ color: '#e67e22', fontSize: '0.8rem' }}>Qtd: {oc.quantidade_divergente}</span>
              )}
              <span style={{ color: '#555', fontSize: '0.8rem', flex: 1 }}>{oc.justificativa}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Componente de linha do pedido (expandível com etiquetas)
// =============================================

interface PedidoRowInlineProps {
  pedido: PaleteDetalhadoPedido;
  etiquetasPendentes: PaleteDetalhadaEtiquetaPendente[];
  isExpanded: boolean;
  onToggle: () => void;
  formatDate: (d: string) => string;
}

const PedidoRowInline: React.FC<PedidoRowInlineProps> = ({ pedido, etiquetasPendentes, isExpanded, onToggle, formatDate }) => {
  const pendentes = pedido.total_volumes_esperado > 0
    ? Math.max(0, pedido.total_volumes_esperado - pedido.total_lido - calcularRemovidosUnicos(pedido.etiquetas))
    : 0;
  const percentual = pedido.total_volumes_esperado > 0
    ? Math.round((pedido.total_lido / pedido.total_volumes_esperado) * 100)
    : null;

  return (
    <>
      <div className={`palete-pedido-row ${isExpanded ? 'expanded' : ''}`} onClick={onToggle}>
        <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} pedido-chevron`}></i>
        <span className="pedido-numero">
          <i className="fas fa-file-invoice" style={{ marginRight: 4, color: '#8e44ad' }}></i>
          {pedido.numero_pedido}
        </span>
        <span className="pedido-badge-bipado">
          <i className="fas fa-check-circle" style={{ marginRight: 3 }}></i>
          {pedido.total_lido} bipado{pedido.total_lido !== 1 ? 's' : ''}
        </span>
        {pendentes > 0 && (
          <span className="pedido-badge-pendente">
            <i className="fas fa-hourglass-half" style={{ marginRight: 3 }}></i>
            {pendentes} pendente{pendentes !== 1 ? 's' : ''}
          </span>
        )}
        {pedido.total_volumes_esperado > 0 && (
          <span style={{ color: '#7f8c8d', fontSize: '0.72rem' }}>de {pedido.total_volumes_esperado}</span>
        )}
        {percentual !== null && (
          <span className={`pedido-percentual ${percentual >= 100 ? 'completo' : percentual >= 50 ? 'parcial' : 'baixo'}`}>
            {percentual}%
          </span>
        )}
        {pedido.total_duplicados > 0 && (
          <span className="pedido-badge-dup">
            {pedido.total_duplicados} dup
          </span>
        )}
        {pendentes === 0 && pedido.total_volumes_esperado > 0 && (
          <span className="pedido-badge-completo">
            <i className="fas fa-check-double" style={{ marginRight: 3 }}></i>Completo
          </span>
        )}
      </div>

      {/* Etiquetas do pedido (bipadas + pendentes) */}
      {isExpanded && (() => {
        // Mesclar bipadas e pendentes em uma lista unificada
        const todasEtiquetas: Array<{
          id: number | string;
          codigo: string;
          resultado: string;
          tipo_leitura: string;
          data: string;
          volume_atual: number | null;
          total_volumes: number | null;
          tipo: 'bipada' | 'pendente';
        }> = [];

        pedido.etiquetas.forEach(etiq => {
          const dados = etiq.dados_decodificados as Record<string, unknown> || {};
          // Extrair volume_atual com fallbacks para diferentes nomes de campo
          const volKeys = Object.keys(dados);
          let volAtual: number | null = null;
          const rawVol = dados.volume_atual ?? dados['Volume Atual'] ?? dados['Volume']
            ?? dados[volKeys.find(k => /volume/i.test(k) && !/total/i.test(k)) || ''];
          if (rawVol != null) { const parsed = parseInt(String(rawVol)); if (!isNaN(parsed)) volAtual = parsed; }

          let totalVol: number | null = null;
          const rawTotal = dados.total_volumes ?? dados['Total de Volumes'] ?? dados['Total Volumes']
            ?? dados[volKeys.find(k => /total/i.test(k) && /volume/i.test(k)) || ''];
          if (rawTotal != null) { const parsed = parseInt(String(rawTotal)); if (!isNaN(parsed)) totalVol = parsed; }

          todasEtiquetas.push({
            id: etiq.id,
            codigo: etiq.codigo_lido,
            resultado: etiq.resultado,
            tipo_leitura: etiq.tipo_leitura,
            data: etiq.lido_em || etiq.criado_em,
            volume_atual: volAtual,
            total_volumes: totalVol,
            tipo: 'bipada',
          });
        });

        etiquetasPendentes.forEach(etq => {
          todasEtiquetas.push({
            id: `pend-${etq.id}`,
            codigo: etq.codigo_barras || '',
            resultado: 'pendente',
            tipo_leitura: '-',
            data: '',
            volume_atual: etq.volume_atual,
            total_volumes: etq.total_volumes_pedido,
            tipo: 'pendente',
          });
        });

        // Ordenar: por volume_atual descendente (maior primeiro), pendentes por último quando sem volume
        todasEtiquetas.sort((a, b) => {
          const va = a.volume_atual ?? 9999;
          const vb = b.volume_atual ?? 9999;
          if (va !== vb) return va - vb;
          // Bipadas antes de pendentes no mesmo volume
          if (a.tipo !== b.tipo) return a.tipo === 'bipada' ? -1 : 1;
          return 0;
        });

        return (
          <div className="pedido-etiquetas-container">
            <table className="pedido-etiquetas-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 28 }}>Código</th>
                  <th>Volume</th>
                  <th>Status</th>
                  <th>Tipo Leitura</th>
                  <th>Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {todasEtiquetas.map((etiq) => {
                  const res = RESULTADO_LABELS[etiq.resultado] || { label: etiq.resultado, color: '#555', icon: 'fas fa-circle' };
                  return (
                    <tr key={etiq.id} style={etiq.tipo === 'pendente' ? { background: '#fef3f3' } : undefined}>
                      <td style={{ paddingLeft: 28, fontFamily: 'monospace', fontSize: '0.78rem', color: etiq.tipo === 'pendente' ? '#c0392b' : '#2c3e50' }}>
                        {etiq.codigo.length > 45 ? etiq.codigo.substring(0, 45) + '...' : etiq.codigo}
                      </td>
                      <td style={{ fontSize: '0.78rem', color: '#555' }}>
                        {etiq.volume_atual ? `${etiq.volume_atual}${etiq.total_volumes ? `/${etiq.total_volumes}` : ''}` : '-'}
                      </td>
                      <td>
                        <span style={{ color: res.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <i className={res.icon} style={{ fontSize: '0.7rem' }}></i>
                          {res.label}
                        </span>
                      </td>
                      <td style={{ color: '#7f8c8d' }}>{etiq.tipo_leitura}</td>
                      <td style={{ color: '#95a5a6' }}>{etiq.data ? formatDate(etiq.data) : '-'}</td>
                    </tr>
                  );
                })}
                {todasEtiquetas.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#95a5a6', padding: 12 }}>
                      Nenhuma etiqueta encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}
    </>
  );
};

export default MontagemPaleteView;
